import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, Chip, Dialog } from '@mui/material';
import MaterialReactTable, { MRT_GlobalFilterTextField as GlobalFilterTextField, MRT_ToggleFiltersButton as ToggleFiltersButton, type MRT_ColumnDef } from 'material-react-table';
import { CloseButton, Loading } from 'components';
import { useJobs } from 'hooks';
import { JobsDTO } from 'api/jobs';
import { jobsColumns } from './consts';
import { JobDetail } from './components';

export const JobsPage: React.FC = () => {
  const [jobDetail, setJobDetail] = useState<JobsDTO | null>(null);
  const [jobsData, setJobsData] = useState<JobsDTO[]>([]);
  const [paginationData, setPaginationData] = useState({
    page: 0,
    size: 10,
    totalRows: 0,
  });

  const { data: response, isLoading } = useJobs(paginationData.size, paginationData.page);

  const allJobs = useMemo(() => jobsData ?? [], [jobsData]);

  useEffect(() => {
    if (response) {
      setJobsData(response?.jobs || []);
      setPaginationData(prev => ({
        ...prev,
        totalRows: response.pageResult.totalElements,
      }));
    }
  }, [response]);

  return (
    <Box sx={{ ml: 3, pr: 3, mt: 3 }}>
      {isLoading ? (
        <Box
          sx={{
            position: 'relative',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            marginTop: '20%',
          }}
        >
          <Loading />
        </Box>
      ) : (
        <MaterialReactTable
          columns={jobsColumns as MRT_ColumnDef<JobsDTO>[]}
          data={allJobs}
          enableRowActions
          manualPagination
          rowCount={paginationData.totalRows}
          onPaginationChange={(updaterOrValue) => {
            const newPagination =
              typeof updaterOrValue === 'function'
                ? updaterOrValue({ pageIndex: paginationData.page, pageSize: paginationData.size })
                : updaterOrValue;

            setPaginationData((prev) => ({
              ...prev,
              page: newPagination.pageIndex,
              size: newPagination.pageSize,
            }));
          }}
          positionActionsColumn='last'
          state={{
            pagination: {
              pageIndex: paginationData.page,
              pageSize: paginationData.size,
            },
          }}
          muiTablePaginationProps={{
            rowsPerPageOptions: [10, 20, 50, 100],
            showFirstButton: true,
            showLastButton: true,
          }}
          renderRowActions={({ row }) => (
            <Button
              variant='text'
              sx={{ padding: '12px' }}
              onClick={() => setJobDetail(row.original)}
            >
              View details
            </Button>
          )}
          renderTopToolbar={({ table }) => (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <h3>
                Jobs <Chip label={paginationData.totalRows} />
              </h3>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <GlobalFilterTextField table={table} />
                <ToggleFiltersButton table={table} />
              </Box>
            </Box>
          )}
          initialState={{
            columnVisibility: { description: false },
            density: 'comfortable',
            showGlobalFilter: true,
          }}
          enableStickyHeader
          muiTableContainerProps={{
            sx: {
              border: '1px solid #C0D0D4',
              borderRadius: '7px',
            },
          }}
          muiTablePaperProps={{
            sx: {
              borderRadius: '10px',
              boxShadow: 'none',
            },
          }}
          muiTableHeadCellProps={{
            sx: {
              borderRight: '1px solid #C0D0D4',
              color: '#054254',
              '& .MuiBadge-root': {
                display: 'none',
              },
            },
          }}
          muiTableBodyCellProps={{
            sx: {
              maxHeight: '60px',
              borderRight: '1px solid #C0D0D4',
              backgroundColor: '#fff',
              padding: 2,
              '& > div': {
                maxHeight: '60px',
              },
            },
          }}
        />
      )}
      <Dialog open={Boolean(jobDetail)} onClose={() => setJobDetail(null)} maxWidth='lg'>
        <Box>
          {jobDetail && (
            <JobDetail jobData={jobDetail}>
              <CloseButton
                onClick={() => setJobDetail(null)}
                sx={{
                  width: '20px',
                  height: '20px',
                  maxHeight: '20px',
                  '& > svg': {
                    width: '20px',
                    height: '20px',
                  },
                }}
              />
            </JobDetail>
          )}
        </Box>
      </Dialog>
    </Box>
  );
};

export default JobsPage;
